import React from 'react';
import ReactDOM from 'react-dom/client';
import Map from './components/Map/Map.jsx';
import Papa from 'papaparse';

import '@codeforamerica/marcomms-design-system/src/components/box.js';
import '@codeforamerica/marcomms-design-system/src/components/quote.js';
import '@codeforamerica/marcomms-design-system/src/components/icon.js';
import '@codeforamerica/marcomms-design-system/src/components/callout.js';
import '@codeforamerica/marcomms-design-system/src/components/page-nav.js';

// React Tooltip CSS
import 'react-tooltip/dist/react-tooltip.css';

function formatStateCount(count) {
  return count === 1 ? '1 state' : `${count} states`;
}

function formatStateNames(names) {
  if (!names.length) {
    return 'No states are currently at this level.';
  }

  return names.join(', ');
}

async function hydrateStageLevelCounts() {
  const countElements = document.querySelectorAll('[data-count-stage][data-count-level]');
  if (!countElements.length) {
    return;
  }

  const stageToColumn = {
    readiness: 'readiness_level',
    piloting: 'piloting_level',
    implementation: 'implementation_level',
    impact: 'impact_level',
  };

  const levelCountsByStage = {
    readiness: { early: 0, developing: 0, established: 0, advanced: 0 },
    piloting: { early: 0, developing: 0, established: 0, advanced: 0 },
    implementation: { early: 0, developing: 0, established: 0, advanced: 0 },
    impact: { early: 0, developing: 0, established: 0, advanced: 0 },
  };

  const stateNamesByStageLevel = {
    readiness: { early: [], developing: [], established: [], advanced: [] },
    piloting: { early: [], developing: [], established: [], advanced: [] },
    implementation: { early: [], developing: [], established: [], advanced: [] },
    impact: { early: [], developing: [], established: [], advanced: [] },
  };

  try {
    const response = await fetch('./assets/data.csv');
    if (!response.ok) {
      throw new Error('Could not load data.csv');
    }

    const csvText = await response.text();
    const { data: rows } = Papa.parse(csvText, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (header) => header.trim(),
    });

    rows.forEach((row) => {
      const stateName = (row.state_name || '').trim();

      Object.entries(stageToColumn).forEach(([stage, column]) => {
        const level = (row[column] || '').trim().toLowerCase();
        if (levelCountsByStage[stage][level] !== undefined) {
          levelCountsByStage[stage][level] += 1;
          if (stateName) {
            stateNamesByStageLevel[stage][level].push(stateName);
          }
        }
      });
    });

    Object.values(stateNamesByStageLevel).forEach((levels) => {
      Object.values(levels).forEach((names) => {
        names.sort((a, b) => a.localeCompare(b));
      });
    });

    countElements.forEach((element) => {
      const stage = element.getAttribute('data-count-stage');
      const level = element.getAttribute('data-count-level');
      const count = levelCountsByStage?.[stage]?.[level];
      const stateNames = stateNamesByStageLevel?.[stage]?.[level] || [];
      const tooltipId = `${stage}-${level}-states-tooltip`;
      const tooltipText = formatStateNames(stateNames);

      if (typeof count !== 'number') {
        return;
      }

      element.textContent = '';

      if (count === 0) {
        element.textContent = formatStateCount(count);

        const nextElement = element.nextElementSibling;
        if (nextElement && nextElement.hasAttribute('data-state-list')) {
          nextElement.remove();
        }

        return;
      }

      const inlineInfo = document.createElement('span');
      inlineInfo.className = 'inline-info';

      const trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.className = 'inline-info__trigger';
      trigger.setAttribute('aria-label', `Show states at ${level} level for ${stage}`);
      trigger.setAttribute('aria-describedby', tooltipId);
      // Native title provides a fallback if custom tooltip styling is clipped or unavailable.
      trigger.setAttribute('title', tooltipText);
      trigger.textContent = formatStateCount(count);

      const tooltip = document.createElement('span');
      tooltip.id = tooltipId;
      tooltip.className = 'inline-info__content';
      tooltip.setAttribute('role', 'tooltip');
      tooltip.textContent = tooltipText;

      inlineInfo.appendChild(trigger);
      inlineInfo.appendChild(tooltip);
      element.appendChild(inlineInfo);

      const nextElement = element.nextElementSibling;
      if (nextElement && nextElement.hasAttribute('data-state-list')) {
        nextElement.remove();
      }
    });
  } catch (error) {
    console.error('Unable to hydrate stage-level counts from CSV:', error);
  }
}

// Main App component
function App() {
  return (
    <>
      <Map />
    </>
  );
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', hydrateStageLevelCounts);
} else {
  hydrateStageLevelCounts();
}

// Render the App
const root = ReactDOM.createRoot(document.getElementById('app'));
root.render(<App />);
